// @/components/reusable/popover.jsx
// Unified Popover system — single source of truth for ALL popovers.
// Modes: click (default) or hover
// Features: spring animation, close-on-scroll, consistent styling

import React, { useEffect, useState, useRef } from 'react';
import * as Headless from '@headlessui/react'
import { AnimatePresence, motion } from 'motion/react';
import clsx from 'clsx'

import { Button } from '@/components/reusable/button'
import { Link } from 'gatsby';
import { dropdownSpring, quickExit } from './animations';

// ============================================================================
// POPOVER ROOT
// ============================================================================

export function Popover({ children, ...props }) {
  return (
    <Headless.Popover {...props}>
      {children}
    </Headless.Popover>
  )
}

// ============================================================================
// POPOVER BUTTON
// ============================================================================

export function PopoverButton({ as = Button, ...props }) {
  return <Headless.Popover.Button as={as} {...props} />
}

// ============================================================================
// POPOVER PANEL — CLICK MODE (default)
// ============================================================================

/**
 * Hook to close popover on scroll.
 *
 * The listener is in the capture phase on purpose: the panel is anchored to its
 * button, so a scrolling ancestor of that button must dismiss it or it drifts
 * away from what it belongs to. Two kinds of scroll must NOT dismiss it: the
 * panel's own (a popover with more content than `max-h-[80vh]` was impossible
 * to scroll — one wheel tick and it closed), and a scroll in an unrelated
 * container. The reduction coach panel auto-scrolls its message list while a
 * reply streams, and with the old "any scroll anywhere" rule every info
 * popover on the page closed the instant it opened while the coach was talking
 * (Tom, 08.09: "clicking the i bubble doesn't do anything while the coach is
 * open"). So: ignore scrolls inside the panel, and outside it close only when
 * the scrolled element contains the popover's button, or the document itself
 * scrolled.
 */
function useCloseOnScroll(close, panelRef) {
  useEffect(() => {
    if (!close) return;
    const handleScroll = (event) => {
      const target = event.target;
      if (!(target instanceof Node)) return close();
      if (panelRef?.current && panelRef.current.contains(target)) return undefined;
      const button = anchorButtonFor(panelRef?.current);
      if (button && target !== document && !target.contains(button)) return undefined;
      return close();
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [close, panelRef]);
}

// The button a Headless popover panel belongs to: the panel element carries an
// id and the button points at it with aria-controls. Null when not resolvable,
// in which case the caller falls back to the strict "any scroll closes" rule.
function anchorButtonFor(panelEl) {
  if (typeof document === 'undefined') return null;
  let el = panelEl;
  while (el && el !== document.body) {
    if (el.id) {
      const button = document.querySelector(`[aria-controls="${el.id}"]`);
      if (button) return button;
    }
    el = el.parentElement;
  }
  return null;
}

/**
 * PopoverPanel — unified panel with click (default) or hover mode.
 *
 * @param {string|Object} anchor - Position: "bottom", "bottom end", or { to, gap, offset }
 * @param {boolean} hover - Enable hover mode (static + AnimatePresence controlled externally)
 * @param {Function} onMouseEnter - Hover mode: mouse enter handler
 * @param {Function} onMouseLeave - Hover mode: mouse leave handler
 */
export function PopoverPanel({ anchor = 'bottom end', className, panelClassName, children, hover, onMouseEnter, onMouseLeave, ...props }) {
  // Hover mode — static panel with spring animation
  if (hover) {
    return (
      <Headless.PopoverPanel
        static
        as={motion.div}
        initial={{ opacity: 0, scale: 0.96, y: -4 }}
        animate={{ opacity: 1, scale: 1, y: 0, transition: dropdownSpring }}
        exit={{ opacity: 0, scale: 0.96, y: -4, transition: quickExit }}
        anchor={typeof anchor === 'string' ? { to: anchor, gap: 8 } : anchor}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        style={{ zIndex: 10000 }}
        className={clsx(
          className,
          'isolate rounded-xl p-4',
          'bg-white',
          'shadow-xl ring-1 ring-zinc-200 ring-inset',
          'max-h-[80vh] overflow-y-auto overscroll-contain',
        )}
        {...props}
      >
        {children}
      </Headless.PopoverPanel>
    );
  }

  // Click mode — standard HeadlessUI panel with close-on-scroll
  return (
    <Headless.Popover.Panel anchor={anchor} className={panelClassName} {...props}>
      {({ close }) => (
        <PopoverPanelInner close={close} className={className}>
          {children}
        </PopoverPanelInner>
      )}
    </Headless.Popover.Panel>
  )
}

function PopoverPanelInner({ close, className, children }) {
  const panelRef = useRef(null);
  useCloseOnScroll(close, panelRef);

  return (
    <AnimatePresence mode="wait">
      <motion.div
        ref={panelRef}
        key="popover-panel"
        initial={{ opacity: 0, scale: 0.96, y: 2 }}
        animate={{ opacity: 1, scale: 1, y: 0, transition: dropdownSpring }}
        exit={{ opacity: 0, scale: 0.96, y: 2, transition: quickExit }}
        style={{ zIndex: 10000 }}
        className={clsx(
          className,
          '[--anchor-gap:--spacing(2)] [--anchor-padding:--spacing(1)] data-[anchor~=start]:[--anchor-offset:-6px] data-[anchor~=end]:[--anchor-offset:6px] sm:data-[anchor~=start]:[--anchor-offset:-0px] sm:data-[anchor~=end]:[--anchor-offset:0px]',
          'isolate w-max rounded-xl p-4',
          'outline-solid outline-0 outline-transparent focus:outline-hidden',
          'bg-white',
          'shadow-lg ring-1 ring-zinc-200 ring-inset dark:ring-inset dark:ring-white/5',
          // Max height with scroll for long content
          'max-h-[80vh] overflow-y-auto overscroll-contain',
        )}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  )
}

// ============================================================================
// useHoverPopover HOOK
// ============================================================================

/**
 * Shared hover logic for any component that needs a hover-triggered popover.
 *
 * Usage:
 *   const hover = useHoverPopover();
 *   <div {...hover.handlers}>
 *     <AnimatePresence>
 *       {hover.isOpen && <PopoverPanel hover {...hover.handlers} />}
 *     </AnimatePresence>
 *   </div>
 */
export function useHoverPopover() {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef(null);

  const onMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const onMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsOpen(false), 100);
  };

  // Close on scroll
  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = () => setIsOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return { isOpen, onMouseEnter, onMouseLeave, handlers: { onMouseEnter, onMouseLeave } };
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

export function PopoverItem({ className, to, ...props }) {
  let classes = clsx(
    className,
    'group cursor-pointer rounded-sm focus:outline-hidden px-2.5 py-1.5',
    'text-left text-black text-sm/6 forced-colors:text-[CanvasText]',
    'hover:bg-black/5 hover:text-black',
    'data-focus:bg-black/5 data-focus:text-black',
    'data-selected:bg-black/5 data-selected:text-black',
    'data-disabled:opacity-50',
    'forced-color-adjust-none forced-colors:data-focus:bg-[Highlight] forced-colors:data-focus:text-[HighlightText] forced-colors:data-focus:*:data-[slot=icon]:text-[HighlightText]',
    'col-span-full flex items-center gap-2.5',
    '*:data-[slot=icon]:col-start-1 *:data-[slot=icon]:row-start-1 *:data-[slot=icon]:-ml-0.5 *:data-[slot=icon]:mr-2.5 *:data-[slot=icon]:size-5 sm:*:data-[slot=icon]:mr-2 sm:*:data-[slot=icon]:size-4',
    '*:data-[slot=icon]:text-zinc-500 data-focus:*:data-[slot=icon]:text-white dark:*:data-[slot=icon]:text-zinc-400 dark:data-focus:*:data-[slot=icon]:text-white',
    '*:data-[slot=avatar]:-ml-1 *:data-[slot=avatar]:mr-2.5 *:data-[slot=avatar]:size-6 sm:*:data-[slot=avatar]:mr-2 sm:*:data-[slot=avatar]:size-5'
  )

  return to ? (
    <Headless.Popover.Button as={Link} to={to} {...props} className={classes} />
  ) : (
    <Headless.Button type="button" className={classes} {...props} />
  )
}

export function PopoverHeader({ className, ...props }) {
  return <div {...props} className={clsx(className, 'col-span-5 px-3.5 pb-1 pt-2.5 sm:px-3')} />
}

export function PopoverSection({ className, ...props }) {
  return (
    <div {...props} className={clsx(className, 'col-span-full supports-[grid-template-columns:subgrid]:grid supports-[grid-template-columns:subgrid]:grid-cols-[auto_1fr_1.5rem_0.5rem_auto]')} />
  )
}

export function PopoverHeading({ className, ...props }) {
  return (
    <h3 {...props} className={clsx(className, 'col-span-full grid grid-cols-[1fr_auto] gap-x-12 px-3.5 pb-1 pt-2 text-sm/5 font-medium text-zinc-500 sm:px-3 sm:text-xs/5 dark:text-zinc-400')} />
  )
}

export function PopoverDivider({ className, ...props }) {
  return (
    <hr {...props} className={clsx(className, 'col-span-full my-2 h-px border-0 bg-black/5 dark:bg-black/10 forced-colors:bg-[CanvasText]')} />
  )
}

export function PopoverLabel({ className, ...props }) {
  return <label {...props} className={clsx(className, 'col-start-2 row-start-1')} />
}

export function PopoverDescription({ className, ...props }) {
  return (
    <p {...props} className={clsx(className, 'col-span-2 col-start-2 row-start-2 text-sm/5 text-black/50 group-data-focus:text-white sm:text-xs/5 dark:text-white/50 forced-colors:group-data-focus:text-[HighlightText]')} />
  )
}
